import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ImageIcon, Upload, Trash2, CheckCircle, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import * as settingsApi from '../endpoints';

export default function CompanyTab({
  companyLogo,
  setCompanyLogo,
  fiscalForm,
  setFiscalForm
}) {
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleFiscalSave = async () => {
    setSaving(true);
    try {
      await settingsApi.updateFiscal(fiscalForm);
      toast.success('Company details saved successfully');
    } catch (error) {
      toast.error('Failed to save company details');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF, or WEBP');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const responseData = await settingsApi.uploadLogo(formData);

      setCompanyLogo(responseData.logo_url);
      toast.success('Company logo uploaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    setUploadingLogo(true);
    try {
      await settingsApi.deleteLogo();
      setCompanyLogo(null);
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Company Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Company Details</CardTitle>
          <CardDescription>Legal and tax-related information for your business</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="commercialName">Commercial Name</Label>
              <Input
                id="commercialName"
                value={fiscalForm.commercial_name || ''}
                onChange={(e) => setFiscalForm(prev => ({ ...prev, commercial_name: e.target.value }))}
                placeholder="e.g., Acme Corp"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="legalName">Legal Business Name</Label>
              <Input
                id="legalName"
                value={fiscalForm.legal_name || ''}
                onChange={(e) => setFiscalForm(prev => ({ ...prev, legal_name: e.target.value }))}
                placeholder="e.g., Acme Corporation LLC"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="address">Address (Street Name and Numbers)</Label>
              <Input
                id="address"
                value={fiscalForm.address || ''}
                onChange={(e) => setFiscalForm(prev => ({ ...prev, address: e.target.value }))}
                placeholder="e.g., 123 Main Street, Suite 400"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="zipCode">Zip Code</Label>
              <Input
                id="zipCode"
                value={fiscalForm.zip_code || ''}
                onChange={(e) => setFiscalForm(prev => ({ ...prev, zip_code: e.target.value }))}
                placeholder="e.g., 90210"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="neighborhood">Neighborhood</Label>
              <Input
                id="neighborhood"
                value={fiscalForm.neighborhood || ''}
                onChange={(e) => setFiscalForm(prev => ({ ...prev, neighborhood: e.target.value }))}
                placeholder="e.g., Beverly Hills"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={fiscalForm.city || ''}
                onChange={(e) => setFiscalForm(prev => ({ ...prev, city: e.target.value }))}
                placeholder="e.g., Los Angeles"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="municipality">Municipality</Label>
              <Input
                id="municipality"
                value={fiscalForm.municipality || ''}
                onChange={(e) => setFiscalForm(prev => ({ ...prev, municipality: e.target.value }))}
                placeholder="e.g., Los Angeles County"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                value={fiscalForm.state || ''}
                onChange={(e) => setFiscalForm(prev => ({ ...prev, state: e.target.value }))}
                placeholder="e.g., California"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={fiscalForm.country || ''}
                onChange={(e) => setFiscalForm(prev => ({ ...prev, country: e.target.value }))}
                placeholder="e.g., United States"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={handleFiscalSave} disabled={saving} className="bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Company Details
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Company Logo Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-1.5">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <CardTitle className="font-heading text-lg">Company Logo</CardTitle>
              {companyLogo && (
                <div className="px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/20 text-xs text-green-600 dark:text-green-400 flex items-center gap-1.5">
                  <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Logo uploaded. It will appear on your invoices and PDF reports.</span>
                </div>
              )}
            </div>
            <CardDescription>Upload your company logo for invoices and reports</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {/* Logo Preview */}
            <div className="w-32 h-32 rounded-xl border-2 border-dashed border-border bg-muted/30 flex items-center justify-center overflow-hidden">
              {companyLogo ? (
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${companyLogo}`}
                  alt="Company Logo"
                  className="w-full h-full object-contain"
                />
              ) : (
                <ImageIcon className="w-10 h-10 text-muted-foreground" />
              )}
            </div>

            {/* Upload Controls */}
            <div className="flex-1 space-y-4">
              <div>
                <p className="text-sm font-medium mb-1">Upload Logo</p>
                <p className="text-xs text-muted-foreground mb-3">
                  Recommended: Square image, at least 200x200px. Max 5MB. Formats: JPEG, PNG, GIF, WEBP
                </p>
                <div className="flex gap-3">
                  <label htmlFor="logo-upload">
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={handleLogoUpload}
                      disabled={uploadingLogo}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="cursor-pointer"
                      disabled={uploadingLogo}
                      asChild
                    >
                      <span>
                        {uploadingLogo ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {uploadingLogo ? 'Uploading...' : 'Choose File'}
                      </span>
                    </Button>
                  </label>
                  {companyLogo && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={handleLogoDelete}
                      disabled={uploadingLogo}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
